import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { Helper } from "../helpers/helper";
import { ToasterService } from "src/app/shared/services/toaster.service";

// TEST_PATCH 12: a stale/invalid stored token (e.g. the same account signed in elsewhere: the server keeps one
// session per account) must drop the session once and land on the login page, not toast "invalid token" forever.
const TOKEN_ERROR = /invalid[-_ ]?token|token[-_ ]?(expired|invalid)|expired[-_ ]?token|session[-_ ]?expired|decrypting[-_ ]?token/i;
let staleTokenRedirecting = false;
function dropStaleSession(): boolean {
  if (!localStorage.getItem("userToken") || location.pathname.startsWith("/auth/")) return false;
  if (!staleTokenRedirecting) {
    staleTokenRedirecting = true;
    ["userToken", "menuItems"].forEach((k) => localStorage.removeItem(k));
    sessionStorage.clear();
    window.location.replace("/auth/login");
  }
  return true;
}

@Injectable()
export class ResInterceptor implements HttpInterceptor {
  constructor(private helper: Helper, private toasterService: ToasterService) {}

  /**
   * Intercepts HTTP requests to handle success and error responses.
   * Displays success or error toast notifications based on the response.
   * Handles specific error status codes like 409 for token expiration.
   *
   * @param req The incoming HTTP request.
   * @param next The next HTTP handler in the chain.
   * @returns An observable of the HTTP event.
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const { isShowSuccessToast, isShowErrorToast } = this.isShowToast(req);

    return next.handle(req).pipe(
      tap((evt) => {
        if (evt instanceof HttpResponse && evt.body) {
          this.helper.showBtnLoader = [];

          const hasBodySuccessKey = evt.body.success !== undefined;
          const isBodySuccessFalse = hasBodySuccessKey && !evt.body.success;
          const hasErrorMessage = !!evt.body.message;

          if ((evt.status === 200 || evt.status === 201) && req.body && isShowSuccessToast && hasErrorMessage && evt.body.success === true) {
            const successMessage = this.helper.translateService.instant(evt.body.message);
            this.toasterService.showNotification("success", successMessage);
          } else if (hasErrorMessage && isBodySuccessFalse && isShowErrorToast !== false) {
            const errorMessage = this.helper.translateService.instant(evt.body.message);
            this.toasterService.showNotification("error", errorMessage);
          }
        }
      }),
      catchError((err: any) => {
        this.helper.showBtnLoader = [];

        if (err instanceof HttpErrorResponse && err.status !== 200) {
          if ((err.error?.reauthRequired === true || TOKEN_ERROR.test(String(err.error?.message ?? ""))) && dropStaleSession()) {
            throw err;
          }
          if (err.status === 409) {
            localStorage.removeItem("userToken");
            this.helper.isUpadtedlocalStorage();
            this.helper.route.navigate(["/auth/login"]).then(() => {
              window.location.reload();
            });
          }

          if (isShowErrorToast !== false) {
            const errorMessage = err.error.message ?? err.message;
            this.toasterService.showNotification("error", errorMessage);
          }
        }

        throw err;
      })
    );
  }

  /**
   * Determines whether to show success or error toast notifications based on the request.
   * Checks the request body and parameters for flags indicating whether to show toasts.
   *
   * @param req The incoming HTTP request.
   * @returns An object containing flags for showing success and error toasts.
   */
  isShowToast(req: HttpRequest<any>): { isShowSuccessToast: boolean; isShowErrorToast: boolean } {
    let isShowSuccessToast = true;
    let isShowErrorToast = true;

    const checkToastFlags = (data: any): void => {
      if (data) {
        if (data.has("isShowSuccessToast") && data.get("isShowSuccessToast") === "false") {
          isShowSuccessToast = false;
        }
        if (data.has("isShowErrorToast") && data.get("isShowErrorToast") === "false") {
          isShowErrorToast = false;
        }
      }
    };

    if (req.body && req.body instanceof FormData) {
      checkToastFlags(req.body);
    } else {
      if (req.body) {
        if (req.body.isShowSuccessToast === false) {
          isShowSuccessToast = false;
        }
        if (req.body.isShowErrorToast === false) {
          isShowErrorToast = false;
        }
      }
      checkToastFlags(req.params);
    }

    return { isShowSuccessToast, isShowErrorToast };
  }
}
